import { type FC, type ReactElement, useState } from 'react'
import { useNavigate } from 'react-router'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { useRejectQueueListing } from '@/features/luxe-submissions/queries/use-reject-queue-listing'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

interface RejectSubmissionDialogProps {
	listingId: string
	trigger: ReactElement
}

export const RejectSubmissionDialog: FC<RejectSubmissionDialogProps> = ({
	listingId,
	trigger
}) => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [open, setOpen] = useState(false)
	const { mutate, isPending } = useRejectQueueListing(listingId, {
		onSuccess: () => {
			setOpen(false)
			navigate(
				routes.LUXE_SUBMISSIONS_INITIAL_EVALUATIONS.handle.fullPath
			)
		}
	})

	return (
		<Dialog onOpenChange={setOpen} open={open}>
			<DialogTrigger render={trigger} />
			<DialogContent className='max-w-120' showCloseButton={false}>
				<DialogHeader>
					{t('luxe_submissions:reject_submission')}
				</DialogHeader>

				<DialogBody className='gap-2'>
					<DialogTitle>
						{t('luxe_submissions:reject_submission_question')}
					</DialogTitle>
					<DialogDescription>
						{t('luxe_submissions:reject_submission_description')}
					</DialogDescription>
				</DialogBody>

				<DialogFooter className='flex flex-row justify-between bg-neutral-white'>
					<DialogClose
						render={
							<ButtonLegacy type='button' variant='tertiary' />
						}
					>
						{t('common:close')}
					</DialogClose>
					<ButtonLegacy
						disabled={isPending}
						onClick={() => mutate()}
						type='button'
						variant='brand'
					>
						{t('common:reject')}
						{isPending && <Spinner />}
					</ButtonLegacy>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
